import { FC, ReactNode } from 'react';
import { LogoBlue, Flex, Box, Heading } from '@dodilio/ui-design-lib';
import { Ribbons, DotLattice } from '@dodilio/ui-design-lib/canvas';
import { TopBar } from './TopBar';

interface HeroShellProps {
    title: string;
    titleSize?: '3' | '4' | '5';
    /**
     * Marketing copy for the hero header. Rendered centered above the form.
     * Kept optional so single-column screens can omit it.
     */
    leftPane?: ReactNode;
    children: ReactNode;
}

// Dressed as the sign-in page, because they are two halves of one journey.
//
// Sign-in puts the form on a frosted panel with the ribbon field passing
// behind it; register had the form lying directly on the page. Same brand,
// two different products — and the panel is the part that does the work:
// it gives the fields an edge to sit against, so the eye lands on the form
// instead of hunting for it against the art.
//
// Ported from ui-keycloak-theme's .dodilFormPanel rather than reinvented, so
// the two stay in step. The deliberate differences: register keeps its own
// headline above the panel (sign-in has one line, register has to say what
// you are signing up for), and the panel is a touch wider because the org
// step needs the room.
export const HeroShell: FC<HeroShellProps> = ({
    title,
    titleSize = '4',
    leftPane,
    children,
}) => {
    return (
        <Box className="register-body">
            {/* Full-bleed procedural art, faded + masked so it dissolves into
                the page toward the form. Replaces the old three.js CloudScene. */}
            {/* The shared brand art, from the design library rather than a
                local copy — sign-in draws the same field, and two hand-kept
                copies of one background is how they drifted apart before. */}
            <div className="heroArtLayer" aria-hidden>
                <Ribbons tone="light" uid="register" intensity={0.55} />
                <DotLattice />
            </div>

            <div className="heroTopBar"><TopBar /></div>

            <Flex
                direction="column"
                align="center"
                px={{ initial: '4', sm: '5' }}
                py={{ initial: '5', sm: '7' }}
                style={{ position: 'relative', zIndex: 2, minHeight: '100vh' }}
            >
                <Flex mb={{ initial: '4', sm: '5' }} justify="center">
                    <img width={48} src={LogoBlue} alt="Dodil" className="heroLogo" />
                </Flex>

                <div className="heroCol">
                    {leftPane ? <div className="heroHeader">{leftPane}</div> : null}

                    <div className="heroCard">
                        <Heading
                            weight="medium"
                            align="center"
                            size={titleSize}
                            mb="4"
                            className="heroFormTitle"
                            style={{ color: 'var(--gray-12)', letterSpacing: '-0.01em' }}
                        >
                            {title}
                        </Heading>
                        {children}
                    </div>
                </div>
            </Flex>

            <style>{`
                /* Background = sign-in's, exactly: the aura wash, the ribbon
                   field, the dot lattice over it. Register had its own
                   procedural art, which is how the two pages ended up looking
                   like different products. */
                .register-body {
                    position: relative;
                    min-height: 100vh;
                    background:
                        radial-gradient(circle at 18% 22%, rgba(140, 120, 255, 0.18) 0%, rgba(255,255,255,0) 52%),
                        radial-gradient(circle at 82% 30%, rgba( 90, 170, 255, 0.16) 0%, rgba(255,255,255,0) 56%),
                        radial-gradient(circle at 50% 92%, rgba(120, 127, 255, 0.10) 0%, rgba(255,255,255,0) 52%),
                        #f7f7ff;
                    background-attachment: fixed;
                }
                .heroArtLayer {
                    position: fixed;
                    inset: 0;
                    z-index: 0;
                    pointer-events: none;
                    overflow: hidden;
                }
                .heroArtLayer > * { position: absolute; inset: 0; }

                .heroTopBar {
                    position: absolute; top: 16px; right: 16px; z-index: 3;
                    max-width: calc(100% - 32px);
                }
                .topChip {
                    display: inline-flex; align-items: center; gap: 8px; max-width: 260px;
                    padding: 4px 12px 4px 4px; border-radius: 999px;
                    border: 1px solid var(--gray-a4); background: rgba(255, 255, 255, 0.8);
                    cursor: pointer; font: inherit;
                }
                .topChip:focus-visible { outline: 2px solid var(--accent-8); outline-offset: 2px; }
                .topChipText { display: flex; flex-direction: column; min-width: 0; text-align: left; line-height: 1.2; }
                @media (max-width: 520px) { .topHint { display: none; } .topChipText { display: none; } .topChip { padding: 4px; } }

                /* Centered single column — inherently mobile-safe, no fixed
                   card height, no horizontal overflow. */
                .heroCol {
                    width: 100%;
                    max-width: 460px;
                    display: flex;
                    flex-direction: column;
                    gap: 28px;
                }

                .heroHeader {
                    text-align: center;
                }

                /* The frosted panel, same recipe as sign-in's .dodilFormPanel.
                   Values are copied deliberately: if the two drift, the two
                   halves of one signup stop looking like one product. */
                .heroCard {
                    position: relative;
                    width: 100%;
                    /* THE reason the card ran off a phone screen. Without
                       this it is content-box, so 26px of padding and 1px of
                       border are added OUTSIDE width:100% — the card renders
                       392px inside a 358px column and overflows by 34px.
                       Nothing else in this file was at fault; the label
                       column and the input min-width were red herrings I
                       chased first. Measured at 390px: 392 -> 358. */
                    box-sizing: border-box;
                    padding: 26px 22px;
                    border-radius: 18px;
                    background: linear-gradient(
                        180deg,
                        rgba(255, 255, 255, 0.94) 0%,
                        rgba(255, 255, 255, 0.88) 100%
                    );
                    backdrop-filter: blur(18px) saturate(1.1);
                    -webkit-backdrop-filter: blur(18px) saturate(1.1);
                    border: 1px solid rgba(115, 125, 255, 0.16);
                    /* Lifted off the art rather than pasted onto it. */
                    box-shadow:
                        0 1px 0 rgba(255, 255, 255, 0.9) inset,
                        0 24px 60px -32px rgba(13, 19, 37, 0.34);
                }



                /* Mobile: keep the header lean — drop the marketing description
                   and bullet list so the form is the immediate focus. */
                @media (max-width: 640px) {
                    .heroPaneDesc,
                    .heroPaneBullets {
                        display: none !important;
                    }
                }

                /* ── Mobile overflow ───────────────────────────────────
                   The card ran off the right edge of a phone. Two causes,
                   both needing fixing or the other still bites:

                   1. every field carries a 140px label column, and
                   2. an <input> has a default intrinsic width of ~180px and
                      a flex item will not shrink below its content unless
                      min-width is cleared.

                   140 + 180 + the card's and the page's padding is wider
                   than a 390px viewport, so the card grew instead of the
                   field shrinking. */
                .fieldLabel { width: 140px; }

                .heroCard input,
                .heroCard .rt-TextFieldRoot,
                .heroCard .rt-TextFieldInput {
                    min-width: 0;
                }
                .heroCard { max-width: 100%; }
                .heroCard, .heroCol { min-width: 0; }

                /* Backstop: nothing here should ever scroll sideways, and a
                   stray overflow on a phone is far worse than clipping. */
                .register-body { overflow-x: clip; }

                @media (max-width: 560px) {
                    /* The label column is the single biggest win — the field
                       gets ~50px back, which is the difference between
                       fitting and not. */
                    .fieldLabel { width: 78px; }
                    .heroCard { padding: 20px 14px; }
                }

                @media (max-width: 480px) {
                    .heroLogo { width: 36px !important; }
                    .heroCol { gap: 18px; }
                    /* Edge-to-edge padding costs more than the inset is worth
                       on a narrow screen. */
                    .heroCard { padding: 22px 16px; }
                }
            `}</style>
        </Box>
    );
};
